import type { MutableRefObject } from "react";
import React from "react";
import { cn } from "@rosette/ts/src/util/cn.js";
import logo from "@rosette/assets/src/logo_text_only.svg";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import type { GameSettings } from "@rosette/ts/src/royalur/model/GameSettings.js";
import Image from "next/image";
import Link from "next/link";
import { getRulesetIcon } from "@/components/icon/getRulesetIcon.js";
import type { GamePlayers } from "@rosette/ts/src/business/api/game/GamePlayers.js";
import type { LobbyStatus } from "@rosette/ts/src/business/game/controller/status/LobbyStatus.js";
import { getGameSettingsReadMorePath } from "@rosette/web_components/src/game/menu/rules/getGameSettingsReadMorePath.js";
import { MENU_PANEL_TITLE } from "@/components/game/menu/MenuLayout.js";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";
import { Card } from "@rosette/web_components/src/ui/Card.js";


interface IntroMenuProps {
    contentRef: MutableRefObject<HTMLDivElement | null>;
    lobbyStatus: LobbyStatus;
    settings: GameSettings;
    players: GamePlayers;
    closeMenu: () => void;
}


export function IntroMenu({
    contentRef, lobbyStatus, settings, players, closeMenu,
}: IntroMenuProps) {
    const gameSettings = Ruleset.getBySettings(settings);
    const icon = getRulesetIcon(gameSettings);
    const readMorePath = getGameSettingsReadMorePath(gameSettings);

    const name = gameSettings.getName() + " Rules";
    const desc = gameSettings.getIntroDesc();

    const isSpectator = players.isSpectator(lobbyStatus);
    return (
        <div className="flex flex-col items-center w-full">
            <Card tone="menu" className="relative max-w-[30rem] pb-3 text-center md:pb-3" ref={contentRef}>
                <h1 className={MENU_PANEL_TITLE}>
                    <InlineIcon size="lg" className="mr-[0.4em]">
                        <Image src={icon} alt={name} />
                    </InlineIcon>
                    <span className="mr-[1.4em]">
                        {name}
                    </span>
                </h1>
                <p className="mb-0 p-3 text-left text-xl text-body">
                    {desc}
                    {" "}
                    <Link href={readMorePath.toString()} target="_blank">
                        Read more about the rules here.
                    </Link>
                </p>

                <div className="flex flex-col items-stretch py-4 gap-4">
                    <Button tone="accent" size="lg" onClick={closeMenu}>
                        {isSpectator ? "View" : "Start"}
                        {" "}
                        Game
                    </Button>
                </div>
                <figure className={cn(
                    "absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[105%]",
                    "w-full max-w-[min(16em,70vw)] m-0 pointer-events-auto",
                )}>
                    <Image src={logo} alt="RoyalUr.net Logo" />
                </figure>
            </Card>
        </div>
    );
}
